import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, Snackbar, Alert,
} from '@mui/material';
import { Field } from '../../UI';
import { acaoAgendarCompromissoController as controller } from '../../../../pages/InspecionarProcesso/AcaoAgendarCompromisso/acaoAgendarCompromissoController';
import DialogSelecionarAntecedentes from './DialogSelecionarAntecedentes';

const MENSAGEM_ERRO_EXCLUIR = 'Não foi possível excluir o compromisso.';

function saoFormFieldsIguais(a, b) {
  return Object.keys(a).every(chave => a[chave] === b[chave]);
}

/**
 * Sub-dialog "Agendar Compromisso" — Etapa 9 do plano `docs/architecture/
 * plano-aw2-1054-aba-historico.md`, 100% novo no protótipo (não existia
 * nenhum equivalente antes). Port de `AcaoAgendarCompromisso.jsx` real,
 * reaproveitando `acaoAgendarCompromissoController` real (mesmo padrão já
 * usado por `MudancaFaseEnvio.jsx`/`acaoEnviarController` na Etapa 8) —
 * evita reimplementar conversão de data (`toAmericanDate`/`toBrazilianDate`)
 * e o payload real (`isAntecedente`).
 *
 * Acionado pelo ícone de calendário ao lado de "EXPANDIR" (reaproveitando o
 * `EventOutlinedIcon` que já existia ali como placeholder não ligado a
 * nada — decisão do usuário — em vez de adicionar um segundo ícone).
 *
 * Botão "Antecedentes" reaproveita `DialogSelecionarAntecedentes.jsx`
 * (mesmo componente da Etapa 8), com seleção ESCOPADA a esta ação
 * (`antecedentesSelecionados` próprio, independente do de
 * `MudancaFaseEnvio.jsx` — mesma separação por ação já usada na tela real,
 * cada uma com seu próprio estado de seleção) — pré-filtrada contra
 * `antecedentesAtivos` ATUAL antes de qualquer chamada (mesmo padrão "IDOR
 * pré-filtro de domínio" da Etapa 8).
 *
 * Guard "sessão monotônica" (`sessaoRef`) incrementado só na transição real
 * fechado→aberto (`foiAbertoRef`, mesmo padrão de `DialogReescrever.jsx`) —
 * este dialog busca o agendamento atual do processo ao abrir (não é
 * sempre-montado sem fetch como `DialogExpandirHistorico.jsx`). Guard
 * síncrono (`enviandoRef`) contra duplo-clique em Agendar/Excluir.
 */
export default function DialogAgendarCompromisso({
  open, processo, antecedentesAtivos, tiposAgendamentosOptions, tiposTitulosAgendamentosOptions,
  onClose, onSucesso,
}) {
  const [formFields, setFormFields] = useState(() => controller.makeFormFields());
  const [errors, setErrors] = useState(controller.makeErrors);
  const [agendamentoAtual, setAgendamentoAtual] = useState(() => controller.makeDefaultAgendamento());
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [limparAberto, setLimparAberto] = useState(false);
  const [excluirAberto, setExcluirAberto] = useState(false);
  const [antecedentesSelecionados, setAntecedentesSelecionados] = useState([]);
  const [antecedentesDialogAberto, setAntecedentesDialogAberto] = useState(false);

  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);
  const foiAbertoRef = useRef(false);

  // Efeito DEDICADO à troca de `idProcesso`, separado do efeito de
  // abertura abaixo — mesmo refinamento já documentado em
  // `src/prototipo/CLAUDE.md` para `DialogTitulo.jsx` (AW2-1034 Etapa 4):
  // `AbaHistorico.jsx` fecha este dialog defensivamente quando o processo
  // muda (`open` vira `false`), mas isso não é a mesma coisa que uma
  // REABERTURA (`foiAbertoRef` só reage a fechado→aberto) — sem este
  // efeito, uma troca de processo com uma chamada em voo (fetch inicial,
  // Agendar, Excluir) não invalidaria a sessão até a próxima abertura
  // genuína, deixando uma janela (ainda que hoje mitigada por outros
  // guards) para uma resposta tardia aplicar dado do processo errado.
  const idProcessoAnteriorRef = useRef(processo?.id);
  useEffect(() => {
    if (idProcessoAnteriorRef.current !== processo?.id) {
      sessaoRef.current += 1;
      idProcessoAnteriorRef.current = processo?.id;
    }
  }, [processo?.id]);

  useEffect(() => {
    if (open && !foiAbertoRef.current) {
      sessaoRef.current += 1;
      const sessaoAlvo = sessaoRef.current;
      const alvo = processo?.id;

      setErro('');
      setLimparAberto(false);
      setExcluirAberto(false);
      setAntecedentesSelecionados([]);
      setAntecedentesDialogAberto(false);
      enviandoRef.current = false;
      setEnviando(false);
      setFormFields(controller.makeFormFields());
      setErrors(controller.makeErrors());
      setAgendamentoAtual(controller.makeDefaultAgendamento());

      if (alvo) {
        setCarregando(true);
        controller.buscarAgendamentoProcesso(alvo).then((resultado) => {
          if (sessaoRef.current !== sessaoAlvo) return;
          setFormFields(controller.makeFormFields(resultado));
          setAgendamentoAtual(resultado);
        }).finally(() => {
          if (sessaoRef.current === sessaoAlvo) setCarregando(false);
        });
      }
    }
    foiAbertoRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, processo?.id]);

  function onChangeCampo(nome) {
    return (event) => {
      const { value } = event.target;
      setFormFields(atual => ({ ...atual, [nome]: value }));
    };
  }

  function limparErro(nome) {
    return () => setErrors(atual => ({ ...atual, [nome]: { error: false, errorMessage: '' } }));
  }

  function onToggleAntecedente(id) {
    setAntecedentesSelecionados(atual => (
      atual.includes(id) ? atual.filter(item => item !== id) : [...atual, id]
    ));
  }

  function onSelecionarTodosAntecedentes(marcarTodos) {
    setAntecedentesSelecionados(marcarTodos ? (antecedentesAtivos || []).map(a => a.id) : []);
  }

  function onCancelarSelecaoAntecedentes() {
    setAntecedentesSelecionados([]);
    setAntecedentesDialogAberto(false);
  }

  useEffect(() => {
    setAntecedentesSelecionados((atual) => {
      if (!antecedentesAtivos) return atual;
      const idsValidos = new Set(antecedentesAtivos.map(a => a.id));
      const filtrado = atual.filter(id => idsValidos.has(id));
      return filtrado.length === atual.length ? atual : filtrado;
    });
  }, [antecedentesAtivos]);

  const desabilitarAgendar = !formFields.dataAgendamento || !formFields.tituloAgendamento
    || !formFields.idTipoAgendamento;
  const desabilitarExcluir = !agendamentoAtual.hasAgendamentoCadastrado;
  const formVazio = controller.makeFormFields({}, true);
  const desabilitarLimpar = saoFormFieldsIguais(formFields, formVazio);

  async function onAgendar() {
    const alvo = processo?.id;
    if (!alvo || enviandoRef.current || desabilitarAgendar) return;

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');

    const antecedentesValidos = antecedentesSelecionados.filter(
      id => (antecedentesAtivos || []).some(a => a.id === id),
    );

    const resposta = await controller.agendarCompromisso(alvo, formFields, errors, false);
    if (sessaoRef.current !== sessaoAlvo) return;

    if (resposta.success) {
      if (antecedentesValidos.length > 0) {
        await Promise.allSettled(
          antecedentesValidos.map(id => controller.agendarCompromisso(id, formFields, errors, true)),
        );
      }
      if (sessaoRef.current !== sessaoAlvo) return;
      if (onSucesso) onSucesso();
      controller.buscarAgendamentoProcesso(alvo).then((resultado) => {
        if (sessaoRef.current === sessaoAlvo) {
          setFormFields(controller.makeFormFields(resultado));
          setAgendamentoAtual(resultado);
        }
      });
    } else {
      setErrors(resposta.errors);
    }

    if (sessaoRef.current === sessaoAlvo) {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  async function onConfirmarExcluir() {
    const alvo = processo?.id;
    setExcluirAberto(false);
    if (!alvo || enviandoRef.current) return;

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);

    const resultado = await controller.excluirAgendamentoCompromisso(alvo);
    if (sessaoRef.current === sessaoAlvo) {
      if (resultado.success) {
        setFormFields(controller.makeFormFields({}, true));
        setAgendamentoAtual(controller.makeDefaultAgendamento());
        if (onSucesso) onSucesso();
      } else {
        setErro(MENSAGEM_ERRO_EXCLUIR);
      }
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  function onConfirmarLimpar() {
    setFormFields(controller.makeFormFields({}, true));
    setErrors(controller.makeErrors());
    setLimparAberto(false);
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Agendamento de Compromisso</DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <Field
              tipo="data"
              label="Data*"
              value={formFields.dataAgendamento}
              onChange={onChangeCampo('dataAgendamento')}
              onFocus={limparErro('dataAgendamento')}
              error={errors.dataAgendamento.error}
              helperText={errors.dataAgendamento.errorMessage}
              disabled={enviando || carregando}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              tipo="select"
              label="Tipo do Compromisso*"
              options={tiposAgendamentosOptions}
              value={String(formFields.idTipoAgendamento)}
              onChange={onChangeCampo('idTipoAgendamento')}
              onFocus={limparErro('idTipoAgendamento')}
              error={errors.idTipoAgendamento.error}
              helperText={errors.idTipoAgendamento.errorMessage}
              disabled={enviando || carregando}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              tipo="select"
              label="Título do Compromisso*"
              options={tiposTitulosAgendamentosOptions}
              value={String(formFields.tituloAgendamento)}
              onChange={onChangeCampo('tituloAgendamento')}
              onFocus={limparErro('tituloAgendamento')}
              error={errors.tituloAgendamento.error}
              helperText={errors.tituloAgendamento.errorMessage}
              disabled={enviando || carregando}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              label="Texto"
              multiline
              minRows={4}
              value={formFields.textoAgendamento}
              onChange={onChangeCampo('textoAgendamento')}
              disabled={enviando || carregando}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <div>
          <Button disabled={desabilitarLimpar || enviando || carregando} onClick={() => setLimparAberto(true)}>
            Limpar
          </Button>
          <Button
            disabled={desabilitarExcluir || enviando || carregando}
            onClick={() => setExcluirAberto(true)}
          >
            Excluir
          </Button>
          <Button
            disabled={enviando || carregando || antecedentesAtivos == null}
            onClick={() => setAntecedentesDialogAberto(true)}
          >
            Antecedentes
          </Button>
        </div>
        <div>
          <Button onClick={onClose}>Fechar</Button>
          <Button
            variant="contained"
            onClick={onAgendar}
            disabled={desabilitarAgendar || enviando || carregando}
          >
            Agendar
          </Button>
        </div>
      </DialogActions>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErro('')}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>

      <Dialog open={limparAberto} onClose={() => setLimparAberto(false)} hideBackdrop>
        <DialogTitle>Limpar campos</DialogTitle>
        <DialogContent>Tem certeza de que deseja descartar essas informações?</DialogContent>
        <DialogActions>
          <Button onClick={() => setLimparAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={onConfirmarLimpar} disabled={enviando}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={excluirAberto} onClose={() => setExcluirAberto(false)} hideBackdrop>
        <DialogTitle>Excluir compromisso</DialogTitle>
        <DialogContent>
          Deseja excluir o Compromisso Agendado? Ao confirmar, o Compromisso será excluído
          permanentemente.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluirAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={onConfirmarExcluir} disabled={enviando}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <DialogSelecionarAntecedentes
        open={antecedentesDialogAberto}
        antecedentes={antecedentesAtivos}
        selecionados={antecedentesSelecionados}
        hideBackdrop
        mensagem="Selecione os antecedentes que também devem receber este agendamento:"
        onToggle={onToggleAntecedente}
        onSelecionarTodos={onSelecionarTodosAntecedentes}
        onClose={() => setAntecedentesDialogAberto(false)}
        onCancelar={onCancelarSelecaoAntecedentes}
      />
    </Dialog>
  );
}

DialogAgendarCompromisso.propTypes = {
  open: PropTypes.bool.isRequired,
  processo: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  antecedentesAtivos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    negociador: PropTypes.string,
    fase: PropTypes.string,
  })),
  tiposAgendamentosOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  tiposTitulosAgendamentosOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func,
};
DialogAgendarCompromisso.defaultProps = {
  processo: null,
  antecedentesAtivos: null,
  tiposAgendamentosOptions: [],
  tiposTitulosAgendamentosOptions: [],
  onSucesso: null,
};
